// ======================================================================
// 📁 File: POViewDrawer.js
// ======================================================================

import React, {
  useEffect,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

import purchaseOrderService
  from "./services/purchaseOrderService";

import {
  formatDate
} from "../../../../../utils/formatUtils";

import PurchaseOrderPrintDrawer
  from "./PurchaseOrderPrintDrawer";

const POViewDrawer = ({
  poId,
  onClose
}) => {

  const [data, setData] =
    useState(null);

  const [trace, setTrace] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [maximized, setMaximized] =
    useState(false);
  
  const [showPrintPreview, setShowPrintPreview] =
  useState(false);

  const navigate = useNavigate();

  const [closing, setClosing] = useState(false);

  const loadDetail = async () => {

    try {

      setLoading(true);

      const res =
        await purchaseOrderService
          .getPODetail(poId);

      if (res.success) {
        setData(res.data);
      }

      const traceRes =
        await purchaseOrderService
          .getPOTrace(poId);

      if (traceRes.success) {
        setTrace(traceRes.data);
      }

    } catch (err) {

      alert(err);

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {

    if (poId) {
      loadDetail();
    }

  }, [poId]);

  const handleApprove = async () => {

    if (
      !window.confirm(
        "Approve this PO?"
      )
    ) {
      return;
    }

    try {

      await purchaseOrderService
        .approvePO(poId);

      alert(
        "PO Approved Successfully"
      );

      loadDetail();

    } catch (err) {

      alert(err);

    }
  };

  const handleSubmitForApproval = async () => {

  if (
    !window.confirm(
      "Submit this PO for approval?"
    )
  ) {
    return;
  }

  try {

    await purchaseOrderService
      .submitForApproval(poId);

    alert(
      "PO Submitted For Approval Successfully"
    );

    loadDetail();

  } catch (err) {

    alert(err);

  }
};

const handleClosePO = async () => {

  const reason = window.prompt(
    "Enter reason for closing Purchase Order"
  );

  if (!reason) {
    return;
  }

  if (
    !window.confirm(
      "Are you sure you want to close this Purchase Order?"
    )
  ) {
    return;
  }

  try {

    setClosing(true);

    const res =
      await purchaseOrderService.closePO(
        data.id,
        reason
      );

    if (res.success) {

      alert(
        "Purchase Order closed successfully"
      );

      loadDetail();

    } else {

      alert(
        res.message ||
        "Failed to close Purchase Order"
      );

    }

  } catch (err) {

    alert(err);

  } finally {

    setClosing(false);

  }
};
  
const handleSend = async () => {

    if (
      !window.confirm(
        "Send this PO to vendor?"
      )
    ) {
      return;
    }

    try {

      await purchaseOrderService
        .sendPO(poId);

      alert(
        "PO Sent To Vendor"
      );

      loadDetail();

    } catch (err) {

      alert(err);

    }
  };

  return (
    <>

      {/* BACKDROP */}

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "rgba(0,0,0,0.4)",
          zIndex: 9998
        }}
        onClick={onClose}
      />

      {/* DRAWER */}

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: maximized
            ? "100%"
            : "1100px",
          height: "100%",
          background: "#fff",
          zIndex: 9999,
          overflowY: "auto",
          boxShadow:
            "-4px 0 12px rgba(0,0,0,0.1)"
        }}
      >

        {/* HEADER */}

        <div
          style={{
            background: "#4f46e5",
            color: "#fff",
            padding: "14px 16px",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center"
          }}
        >

          <h5
            style={{
              margin: 0
            }}
          >
            Purchase Order
            {data?.po_no
              ? ` - ${data.po_no}`
              : ""}
          </h5>

          <div>

            <button
              onClick={() =>
                setMaximized(
                  !maximized
                )
              }
              style={{
                background:
                  "none",
                border:
                  "none",
                color:
                  "#fff",
                fontSize:
                  "18px",
                marginRight:
                  "10px",
                cursor:
                  "pointer"
              }}
            >
              {maximized
                ? "❐"
                : "⛶"}
            </button>

            <button
              onClick={onClose}
              style={{
                background:
                  "none",
                border:
                  "none",
                color:
                  "#fff",
                fontSize:
                  "20px",
                cursor:
                  "pointer"
              }}
            >
              ×
            </button>

          </div>

        </div>

        {/* BODY */}

        <div
          style={{
            padding: 16
          }}
        >

          {loading && (
            <p>Loading...</p>
          )}

          {!loading &&
            data && (
              <>
      {/* ===================================================== */}
      {/* PURCHASE ORDER INFORMATION */}
      {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          Purchase Order Information
        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-3 mb-3">
              <label className="fw-bold">
                PO No
              </label>
              <div>
                {data.po_no}
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <label className="fw-bold">
                PO Date
              </label>
              <div>
                {data.po_date
                  ? formatDate(
                      data.po_date
                    )
                  : "-"}
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <label className="fw-bold">
                Status
              </label>
              <div>
                {data.status_code}
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <label className="fw-bold">
                Currency
              </label>
              <div>
                {data.currency ||
                  "INR"}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="fw-bold">
                Vendor
              </label>
              <div>
                {data.vendor_name ||
                  "-"}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="fw-bold">
                Purpose
              </label>
              <div>
                {data.procurement_purpose ||
                  "-"}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="fw-bold">
                Delivery Date
              </label>
              <div>
                {data.delivery_date
                  ? formatDate(
                      data.delivery_date
                    )
                  : "-"}
              </div>
            </div>

               </div>

    </div>

  </div>

  {/* ===================================================== */}
  {/* VENDOR INFORMATION */}
  {/* ===================================================== */}

  <div className="card mb-3">

    <div className="card-header fw-bold">
      Vendor Information
    </div>

    <div className="card-body">

      <div className="row">

        <div className="col-md-6">

          <table className="table table-sm">

            <tbody>

              <tr>
                <th>Vendor Code</th>
                <td>
                  {data.vendor_detail?.bp_code || "-"}
                </td>
              </tr>

              <tr>
                <th>Vendor Name</th>
                <td>
                  {data.vendor_detail?.bp_name ||
                    data.vendor_name}
                </td>
              </tr>

              <tr>
                <th>GST Number</th>
                <td>
                  {
                    data.vendor_detail?.taxes?.[0]
                      ?.registration_no || "-"
                  }
                </td>
              </tr>

            </tbody>

          </table>

        </div>

        <div className="col-md-6">

          <table className="table table-sm">

            <tbody>

              <tr>
                <th>Contact Person</th>
                <td>
                  {
                    data.vendor_detail?.contacts?.[0]
                      ?.contact_name || "-"
                  }
                </td>
              </tr>

              <tr>
                <th>Mobile</th>
                <td>
                  {
                    data.vendor_detail?.contacts?.[0]
                      ?.mobile || "-"
                  }
                </td>
              </tr>

              <tr>
                <th>Email</th>
                <td>
                  {
                    data.vendor_detail?.contacts?.[0]
                      ?.email || "-"
                  }
                </td>
              </tr>

            </tbody>

          </table>

        </div>

      </div>

      <hr />

      <div>

        <strong>Address</strong>

        <div className="mt-2">

          {
            data.vendor_detail?.addresses?.[0]
              ?.line1
          }

          {" "}

          {
            data.vendor_detail?.addresses?.[0]
              ?.line2
          }

          <br />

          {
            data.vendor_detail?.addresses?.[0]
              ?.city
          }

          {", "}

          {
            data.vendor_detail?.addresses?.[0]
              ?.state
          }

          {" - "}

          {
            data.vendor_detail?.addresses?.[0]
              ?.pincode
          }

        </div>

      </div>

    </div>

  </div>

  {/* ===================================================== */}
  {/* REFERENCES */}
  {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          References
        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-3">
              <label className="fw-bold">
                PR
              </label>
              <div>
                {data.pr_id || "-"}
              </div>
            </div>

            <div className="col-md-3">
              <label className="fw-bold">
                Indent
              </label>
              <div>
                {data.indent_id ||
                  "-"}
              </div>
            </div>

            <div className="col-md-3">
              <label className="fw-bold">
                Project
              </label>
              <div>
                {data.project_id ||
                  "-"}
              </div>
            </div>

            <div className="col-md-3">
              <label className="fw-bold">
                Order
              </label>
              <div>
                {data.order_id ||
                  "-"}
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
{/* COMMERCIAL INFORMATION */}
{/* ===================================================== */}

<div className="card mb-3">

  <div className="card-header fw-bold">
    Commercial Information
  </div>

  <div className="card-body">

    <div className="row">

      <div className="col-md-6">

        <label className="fw-bold">
          Delivery Terms
        </label>

        <div className="border rounded p-2 mt-1">
          {data.delivery_terms || "-"}
        </div>

      </div>

      <div className="col-md-6">

        <label className="fw-bold">
          Payment Terms
        </label>

        <div className="border rounded p-2 mt-1">
          {data.payment_terms || "-"}
        </div>

      </div>

    </div>

    <div className="mt-3">

      <label className="fw-bold">
        Remarks
      </label>

      <div className="border rounded p-2 mt-1">
        {data.remarks || "-"}
      </div>

    </div>

  </div>

</div>
      
      {/* ===================================================== */}
      {/* MATERIAL DETAILS */}
      {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          Material Details
        </div>

        <div className="card-body p-0">

          <table className="table table-bordered table-sm mb-0">

            <thead>

              <tr>

                <th>
                  Material
                </th>

                <th>
                  HSN/SAC
                </th>

                <th>
                  Qty
                </th>

                <th>
                  UOM
                </th>

                <th>
                  Rate
                </th>

                <th>
                  Gross
                </th>

                <th>
                  Disc %
                </th>

                <th>
                  Discount
                </th>

                <th>
                  Taxable
                </th>

                <th>
                  GST %
                </th>

                <th>
                  GST Amt
                </th>

                <th>
                  Total
                </th>

              </tr>

            </thead>

            <tbody>

              {data.lines?.map(
                (
                  l,
                  idx
                ) => (

                  <tr
                    key={idx}
                  >

                    <td>
                      {
                        l.material_name
                      }
                    </td>

                    <td>
                      {l.tax_code ||
                        "-"}
                    </td>

                    <td>
                      {Number(
                        l.qty ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {l.uom}
                    </td>

                    <td>
                      {Number(
                        l.rate ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.gross_amount ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.discount_percent ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.discount_amount ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.taxable_amount ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.gst_rate ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.gst_amount ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                    <td>
                      {Number(
                        l.line_total ||
                          0
                      ).toFixed(
                        2
                      )}
                    </td>

                  </tr>

                )
              )}

              {(!data.lines ||
                data.lines
                  .length ===
                  0) && (
                <tr>
                  <td
                    colSpan="12"
                  >
                    No
                    Materials
                    Found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

      </div>
            {/* ===================================================== */}
      {/* ADDITIONAL CHARGES */}
      {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          Additional Charges
        </div>

        <div className="card-body p-0">

          <table className="table table-bordered table-sm mb-0">

            <thead>
              <tr>

                <th>Charge Type</th>

                <th>Payer</th>

                <th>Customer</th>

                <th>Amount</th>

                <th>GST %</th>

                <th>GST Amount</th>

                <th>Total</th>

                <th>Remarks</th>

              </tr>
            </thead>

            <tbody>

              {data.charges?.length > 0 ? (

                data.charges.map((c, idx) => (

                  <tr key={idx}>

                    <td>
                      {c.charge_type}
                    </td>

                    <td>
                      {c.payer_type}
                    </td>

                    <td>
                      {c.customer_name ||
                        c.customer_id ||
                        "-"}
                    </td>

                    <td>
                      {Number(
                        c.charge_amount || 0
                      ).toFixed(2)}
                    </td>

                    <td>
                      {Number(
                        c.gst_rate || 0
                      ).toFixed(2)}
                    </td>

                    <td>
                      {Number(
                        c.gst_amount || 0
                      ).toFixed(2)}
                    </td>

                    <td>
                      {Number(
                        c.total_amount || 0
                      ).toFixed(2)}
                    </td>

                    <td>
                      {c.remarks || "-"}
                    </td>

                  </tr>

                ))

              ) : (

                <tr>
                  <td colSpan="8">
                    No Additional Charges
                  </td>
                </tr>

              )}

            </tbody>

          </table>

        </div>

      </div>

      {/* ===================================================== */}
{/* GST SUMMARY */}
{/* ===================================================== */}

<div className="card mb-3">

  <div className="card-header fw-bold">
    GST Summary
  </div>

  <div className="card-body">

    <div className="row">

      <div className="col-md-6">

        <table className="table table-sm">

          <tbody>

            <tr>
              <th>Material Taxable Amount</th>
              <td className="text-end">
                {Number(
                  data.material_taxable_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Material GST Amount</th>
              <td className="text-end">
                {Number(
                  data.material_gst_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

          </tbody>

        </table>

      </div>

      <div className="col-md-6">

        <table className="table table-sm">

          <tbody>

            <tr>
              <th>Charge GST Amount</th>
              <td className="text-end">
                {Number(
                  data.charge_gst_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Total GST</th>
              <td
                className="text-end fw-bold"
              >
                {Number(
                  data.tax_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

          </tbody>

        </table>

      </div>

    </div>

  </div>

</div>
      
{/* ===================================================== */}
{/* DOCUMENT TOTALS */}
{/* ===================================================== */}

<div className="card mb-3">

  <div className="card-header fw-bold">
    Document Totals
  </div>

  <div className="card-body">

    <div className="row">

      <div className="col-md-6">

        <table className="table table-sm">

          <tbody>

            <tr>
              <th>Material Gross Amount</th>
              <td className="text-end">
                {Number(
                  data.material_gross_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Material Discount</th>
              <td className="text-end">
                {Number(
                  data.material_discount_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Material Taxable</th>
              <td className="text-end">
                {Number(
                  data.material_taxable_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

          </tbody>

        </table>

      </div>

      <div className="col-md-6">

        <table className="table table-sm">

          <tbody>

            <tr>
              <th>Material GST</th>
              <td className="text-end">
                {Number(
                  data.material_gst_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Charges</th>
              <td className="text-end">
                {Number(
                  data.charge_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr>
              <th>Charge GST</th>
              <td className="text-end">
                {Number(
                  data.charge_gst_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}
              </td>
            </tr>

            <tr className="table-success">

              <th>Net Amount</th>

              <td className="text-end fw-bold">

                ₹ {Number(
                  data.net_amount || 0
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2
                })}

              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  </div>

</div>
      {/* ===================================================== */}
      {/* COMMERCIAL SUMMARY */}
      {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          Commercial Summary
        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-6">

              <table className="table table-sm">

                <tbody>

                  <tr>
                    <th>
                      Material Gross Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.material_gross_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Material Discount Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.material_discount_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Material Taxable Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.material_taxable_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Material GST Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.material_gst_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                </tbody>

              </table>

            </div>

            <div className="col-md-6">

              <table className="table table-sm">

                <tbody>

                  <tr>
                    <th>
                      Charge Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.charge_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Charge GST Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.charge_gst_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Total Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.total_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr>
                    <th>
                      Tax Amount
                    </th>
                    <td className="text-end">
                      {Number(
                        data.tax_amount || 0
                      ).toFixed(2)}
                    </td>
                  </tr>

                  <tr className="table-primary">

                    <th>
                      NET PURCHASE VALUE
                    </th>

                    <td className="text-end fw-bold">

                      {Number(
                        data.net_amount || 0
                      ).toFixed(2)}

                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

          </div>

        </div>

      </div>
            {/* ===================================================== */}
      {/* AUDIT INFORMATION */}
      {/* ===================================================== */}

      <div className="card mb-3">

        <div className="card-header fw-bold">
          Audit Information
        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-3">

              <label className="fw-bold">
                Created By
              </label>

              <div>
                {data.created_by_name || "-"}
              </div>

            </div>

            <div className="col-md-3">

              <label className="fw-bold">
                Created On
              </label>

              <div>
                {data.created_at
                  ? formatDate(
                      data.created_at
                    )
                  : "-"}
              </div>

            </div>

            <div className="col-md-3">

              <label className="fw-bold">
                Approved By
              </label>

              <div>
                {data.approved_by_name || "-"}
              </div>

            </div>

            <div className="col-md-3">

              <label className="fw-bold">
                Approved On
              </label>

              <div>
                {data.approved_at
                  ? formatDate(
                      data.approved_at
                    )
                  : "-"}
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* DOCUMENT TRACE */}
      {/* ===================================================== */}

      {trace && (

        <div className="card mb-3">

          <div className="card-header fw-bold">
            Document Trace
          </div>

          <div className="card-body">

            <div className="row">

                <div className="col-md-3">

                  <div className="card border-primary">
                    <div className="card-body text-center">
                      <h6>Indent</h6>
                      <div>
                        {trace.indent_id || "-"}
                      </div>
                    </div>
                  </div>

                </div>

                <div className="col-md-3">

                  <div className="card border-primary">
                    <div className="card-body text-center">
                      <h6>PR</h6>
                      <div>
                        {trace.pr_id || "-"}
                      </div>
                    </div>
                  </div>

                </div>

                <div className="col-md-3">

                  <div className="card border-success">
                    <div className="card-body text-center">
                      <h6>RFQ</h6>
                      <div>
                        {trace.rfq_id || "-"}
                      </div>
                    </div>
                  </div>

                </div>

                <div className="col-md-3">

                  <div className="card border-dark">
                    <div className="card-body text-center">
                      <h6>PO</h6>
                      <div>
                        {data.po_no}
                      </div>
                    </div>
                  </div>

                </div>

              </div>

          </div>

        </div>

      )}

              </>
          )}

        </div>

        {/* ===================================================== */}
        {/* FOOTER */}
        {/* ===================================================== */}

        <div
          style={{
            padding: 12,
            borderTop:
              "1px solid #ddd",
            textAlign: "right"
          }}
        >

          <button
            className="btn btn-outline-secondary me-2"
            onClick={() =>
              setShowPrintPreview(true)
            }
          >
            Print And PDF
          </button>

          

          <button
            className="btn btn-outline-primary me-2"
            onClick={() =>
              alert(
                "Email Coming Soon"
              )
            }
          >
            Send
          </button>

          <button
            className="btn btn-secondary me-2"
            onClick={onClose}
          >
            Close
          </button>

          {data?.status_code ===
            "DRAFT" && (

            <button
              className="btn btn-success me-2"
              onClick={
                handleSubmitForApproval
              }
            >
              Submit For Approval
            </button>

          )}

          {data?.status_code ===
            "APPROVED" && (

            <button
              className="btn btn-primary"
              onClick={
                handleSend
              }
            >
              Mark as Sent To Vendor
            </button>

          )}
           
          {["DRAFT", "PENDING_APPROVAL", "APPROVED", "SENT"].includes(
            data?.status_code
          ) && (
            <button
              className="btn btn-danger me-2"
              onClick={handleClosePO}
            >
              Close PO
            </button>
          )}

             </div>

    </div>

    {showPrintPreview && (
      <PurchaseOrderPrintDrawer
        poId={poId}
        poData={data}
        traceData={trace}
        onClose={() =>
          setShowPrintPreview(false)
        }
      />
    )}

  </>

);
};

export default POViewDrawer;